/* eslint-disable react-hooks/exhaustive-deps */
import React, { createContext, useContext, useState, useEffect, useRef } from 'react';
import { AudioPlayer } from '@mediagrid/capacitor-native-audio';
import { LocalNotifications } from '@capacitor/local-notifications';
import { isNativeAndroidAudio } from '../config/platform';
import { MeditationResponse, meditationService } from '../services/meditationService';

// Keys for localStorage
const STORAGE_KEYS = {
  MEDITATION: 'luminous_last_meditation',
  POSITION: 'luminous_last_position',
  TIMESTAMP: 'luminous_last_timestamp'
};

const NATIVE_AUDIO_ID = 'luminous-meditation';

interface PlaybackContextType {
  currentMeditation: MeditationResponse | null;
  isPlaying: boolean;
  currentTime: number;
  duration: number;
  progress: number;
  volume: number;
  isMiniPlayerVisible: boolean;
  audioRef: React.RefObject<HTMLAudioElement>;
  /** Android native player readiness; always false on web/iOS. */
  isAudioReady: boolean;
  /** Incremented when native audio ends; 0 on web/iOS. */
  audioEndedToken: number;
  /** Hash of the meditation that most recently ended on Android. */
  audioEndedHash: string | null;

  // Control methods
  registerMeditation: (meditation: MeditationResponse) => void;
  playMeditation: (meditation: MeditationResponse) => void;
  pauseMeditation: () => void;
  resumeMeditation: () => void;
  stopMeditation: () => void;
  setCurrentTime: (time: number) => void;
  setVolume: (volume: number) => void;
  togglePlayPause: () => void;
  hideMiniPlayer: () => void;
  showMiniPlayer: () => void;
}

export const PlaybackContext = createContext<PlaybackContextType | undefined>(undefined);

export const PlaybackProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [currentMeditation, setCurrentMeditation] = useState<MeditationResponse | null>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [progress, setProgress] = useState(0);
  const [volume, setVolume] = useState(80); // Default volume at 80%
  const [isMiniPlayerVisible, setIsMiniPlayerVisible] = useState(false);
  const [isAudioReady, setIsAudioReady] = useState(false);
  const [audioEndedToken, setAudioEndedToken] = useState(0);
  const [audioEndedHash, setAudioEndedHash] = useState<string | null>(null);

  // Flag to track if we've already attempted to restore the state on initial load
  const hasAttemptedRestore = useRef(false);
  
  const audioRef = useRef<HTMLAudioElement>(null);
  const hasTrackedPlay = useRef(false);
  const saveStateTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const wasPlayingBeforeHide = useRef(false);
  const isPlayingRef = useRef(false);

  // Native Android player state
  const nativeAudioCreatedRef = useRef(false);
  const notificationPermissionRequestedRef = useRef(false);
  const nativePendingSeekRef = useRef<number | null>(null);
  // Tracks an in-flight destroy so create() can await the REAL native teardown,
  // not just the JS boolean flag (the plugin defers removal to the main looper).
  const nativeDestroyPromiseRef = useRef<Promise<void> | null>(null);
  // Native stall detection: last observed position + a repeat counter, so the
  // 1s poll can spot a frozen stream (the plugin exposes no error callback).
  const nativeLastPositionRef = useRef<number>(-1);
  const nativeStallTicksRef = useRef(0);
  const nativeReconnectingRef = useRef(false);

  // HTML5 stream stall recovery state
  const stallRecoveryTimerRef = useRef<NodeJS.Timeout | null>(null);
  const stallRecoveryAttemptsRef = useRef(0);
  // Set while recoverStalledStream() is reloading the element, so the pause
  // event that load() emits is not mistaken for a real stop.
  const isRecoveringHtml5Ref = useRef(false);

  useEffect(() => {
    isPlayingRef.current = isPlaying;
  }, [isPlaying]);

  // Save current playback state to localStorage
  const savePlaybackState = () => {
    if (!currentMeditation) return;
    
    try {
      // Save meditation data
      localStorage.setItem(STORAGE_KEYS.MEDITATION, JSON.stringify(currentMeditation));
      
      // Save current position
      localStorage.setItem(STORAGE_KEYS.POSITION, currentTime.toString());
      
      // Save timestamp of when this was stored
      localStorage.setItem(STORAGE_KEYS.TIMESTAMP, Date.now().toString());
    } catch (error) {
      console.error('Error saving playback state to localStorage:', error);
    }
  };
  
  // Load saved playback state from localStorage
  const loadPlaybackState = () => {
    try {
      // Check if we have saved data
      const savedMeditationJson = localStorage.getItem(STORAGE_KEYS.MEDITATION);
      const savedPosition = localStorage.getItem(STORAGE_KEYS.POSITION);
      const savedTimestamp = localStorage.getItem(STORAGE_KEYS.TIMESTAMP);
      
      if (!savedMeditationJson || !savedPosition || !savedTimestamp) return false;
      
      // Parse the saved data
      const savedMeditation = JSON.parse(savedMeditationJson) as MeditationResponse;
      const position = parseFloat(savedPosition);
      const timestamp = parseInt(savedTimestamp, 10);
      
      // Check if the saved state is too old (older than 24 hours)
      const MAX_AGE = 24 * 60 * 60 * 1000; // 24 hours in milliseconds
      const now = Date.now();
      if (now - timestamp > MAX_AGE) {
        // Clear outdated storage
        localStorage.removeItem(STORAGE_KEYS.MEDITATION);
        localStorage.removeItem(STORAGE_KEYS.POSITION);
        localStorage.removeItem(STORAGE_KEYS.TIMESTAMP);
        return false;
      }
      
      // Update state with saved values
      setCurrentMeditation(savedMeditation);
      setCurrentTime(position);
      setProgress((position / (savedMeditation.duration || 1)) * 100);
      setDuration(savedMeditation.duration || 0);
      setIsMiniPlayerVisible(true);
      
      // Set the audio source but don't play it yet. The native Android player
      // is created lazily on the next play, seeking to the saved position.
      if (!isNativeAndroidAudio && audioRef.current) {
        audioRef.current.src = savedMeditation.audioUrl || '';
        audioRef.current.currentTime = position;
        audioRef.current.load();
      }

      return true;
    } catch (error) {
      console.error('Error loading playback state from localStorage:', error);
      return false;
    }
  };

  // --- Native Android playback (foreground media service) ---

  // Android 13+ suppresses the media notification (and therefore the lock-screen
  // / shade controls) until POST_NOTIFICATIONS is granted. Request it once, the
  // first time the user starts native playback, so the prompt is contextual.
  // Audio still plays if denied — only the controls are absent.
  const ensureNotificationPermission = async () => {
    if (!isNativeAndroidAudio || notificationPermissionRequestedRef.current) return;
    notificationPermissionRequestedRef.current = true;
    try {
      const { display } = await LocalNotifications.checkPermissions();
      if (display === 'prompt' || display === 'prompt-with-rationale') {
        await LocalNotifications.requestPermissions();
      }
    } catch (error) {
      console.error('Error requesting notification permission:', error);
    }
  };

  const destroyNativeAudio = async () => {
    // Already torn down, but a previous destroy may still be settling on the
    // native main looper — await it so callers see a truly-free player.
    if (!nativeAudioCreatedRef.current) {
      if (nativeDestroyPromiseRef.current) await nativeDestroyPromiseRef.current;
      return;
    }
    nativeAudioCreatedRef.current = false;
    setIsAudioReady(false);
    const destroyPromise = (async () => {
      try {
        await AudioPlayer.destroy({ audioId: NATIVE_AUDIO_ID });
      } catch (error) {
        console.error('Error destroying native audio:', error);
      }
    })();
    nativeDestroyPromiseRef.current = destroyPromise;
    try {
      await destroyPromise;
    } finally {
      // Only clear if no newer destroy superseded this one
      if (nativeDestroyPromiseRef.current === destroyPromise) {
        nativeDestroyPromiseRef.current = null;
      }
    }
  };

  const createNativeAudio = async (meditation: MeditationResponse) => {
    await destroyNativeAudio();
    // Guard against a fire-and-forget destroy (onAudioEnd / stopMeditation) that
    // is still settling — create() must not run while the source exists.
    if (nativeDestroyPromiseRef.current) await nativeDestroyPromiseRef.current;

    setIsAudioReady(false);

    await AudioPlayer.create({
      audioId: NATIVE_AUDIO_ID,
      audioSource: meditation.audioUrl || '',
      friendlyTitle: meditation.title,
      useForNotification: true,
      // Use the bundled asset, not a network URL. The plugin fetches over the
      // network only when artworkSource starts with "https:"; any other value is
      // opened from the app's bundled assets ("public/" + value), which always
      // loads (no dependency on prod being reachable at play time). The file is
      // synced to android/app/src/main/assets/public/logo512.png by Capacitor.
      artworkSource: 'logo512.png',
      isBackgroundMusic: false,
      loop: false,
    });

    // Listeners must be registered before initialize()
    await AudioPlayer.onAudioReady({ audioId: NATIVE_AUDIO_ID }, async () => {
      try {
        const { duration: nativeDuration } = await AudioPlayer.getDuration({ audioId: NATIVE_AUDIO_ID });
        if (nativeDuration) {
          setDuration(nativeDuration);
        }
        const seekTo = nativePendingSeekRef.current;
        nativePendingSeekRef.current = null;
        if (seekTo && seekTo > 0) {
          await AudioPlayer.seek({ audioId: NATIVE_AUDIO_ID, timeInSeconds: seekTo });
        }
        setIsAudioReady(true);
      } catch (error) {
        console.error('Error preparing native audio:', error);
      }
    });

    await AudioPlayer.onAudioEnd({ audioId: NATIVE_AUDIO_ID }, () => {
      setIsPlaying(false);
      setCurrentTime(0);
      setProgress(0);
      if (meditation.hash) {
        setAudioEndedHash(meditation.hash);
        setAudioEndedToken(token => token + 1);
      }
      // Release the player so the foreground service and notification stop
      destroyNativeAudio();
    });

    await AudioPlayer.onPlaybackStatusChange({ audioId: NATIVE_AUDIO_ID }, (result) => {
      // The OS (Doze, OEM battery managers) can pause audio without the UI
      // knowing — mirror the true native state so the controls never desync
      setIsPlaying(result.status === 'playing');
    });

    await AudioPlayer.initialize({ audioId: NATIVE_AUDIO_ID });
    nativeAudioCreatedRef.current = true;
    // Reset stall detection for the new source
    nativeLastPositionRef.current = -1;
    nativeStallTicksRef.current = 0;

    try {
      await AudioPlayer.setVolume({ audioId: NATIVE_AUDIO_ID, volume: volume / 100 });
    } catch (error) {
      console.error('Error setting native audio volume:', error);
    }
  };

  const syncNativePlaybackState = async () => {
    if (!nativeAudioCreatedRef.current) return;
    try {
      const { isPlaying: nativeIsPlaying } = await AudioPlayer.isPlaying({ audioId: NATIVE_AUDIO_ID });
      const { currentTime: nativeTime } = await AudioPlayer.getCurrentTime({ audioId: NATIVE_AUDIO_ID });
      const { duration: nativeDuration } = await AudioPlayer.getDuration({ audioId: NATIVE_AUDIO_ID });
      setIsPlaying(nativeIsPlaying);
      setCurrentTime(nativeTime);
      if (nativeDuration) {
        setDuration(nativeDuration);
      }
      setProgress((nativeTime / (nativeDuration || 1)) * 100);
    } catch (error) {
      console.error('Error syncing native playback state:', error);
    }
  };

  // The native plugin exposes no error/stall callback, so recover a frozen
  // stream by rebuilding the source at the last known position. Used when the
  // 1s poll sees the position stuck while we still believe we're playing (e.g.
  // ExoPlayer went IDLE after the network dropped beyond its retry window).
  const reconnectNativeStream = async (meditation: MeditationResponse, resumeAt: number) => {
    if (nativeReconnectingRef.current) return;
    nativeReconnectingRef.current = true;
    try {
      nativePendingSeekRef.current = resumeAt > 0 ? resumeAt : null;
      await createNativeAudio(meditation);
      await AudioPlayer.play({ audioId: NATIVE_AUDIO_ID });
      setIsPlaying(true);
    } catch (error) {
      console.error('Error reconnecting native stream:', error);
    } finally {
      nativeReconnectingRef.current = false;
    }
  };

  // Poll the native player for position while playing (replaces the HTML5
  // timeupdate event on Android). Also detects a frozen stream and reconnects.
  useEffect(() => {
    if (!isNativeAndroidAudio || !isPlaying) return;

    const interval = setInterval(async () => {
      if (!nativeAudioCreatedRef.current || nativeReconnectingRef.current) return;
      try {
        const { currentTime: nativeTime } = await AudioPlayer.getCurrentTime({ audioId: NATIVE_AUDIO_ID });
        const { duration: nativeDuration } = await AudioPlayer.getDuration({ audioId: NATIVE_AUDIO_ID });
        setCurrentTime(nativeTime);
        if (nativeDuration) {
          setDuration(nativeDuration);
        }
        setProgress((nativeTime / (nativeDuration || 1)) * 100);

        // Stall detection: position not advancing while we think we're playing.
        // Ignore the natural end (position at/after duration).
        const nearEnd = nativeDuration > 0 && nativeTime >= nativeDuration - 1;
        if (nativeTime === nativeLastPositionRef.current && !nearEnd) {
          nativeStallTicksRef.current += 1;
        } else {
          nativeStallTicksRef.current = 0;
          nativeLastPositionRef.current = nativeTime;
        }

        // ~8s of no progress → treat as a dead stream and rebuild at last pos.
        if (nativeStallTicksRef.current >= 8 && currentMeditation) {
          nativeStallTicksRef.current = 0;
          reconnectNativeStream(currentMeditation, nativeTime);
        }
      } catch (error) {
        // Player may be mid-create/destroy; skip this tick
      }
    }, 1000);

    return () => clearInterval(interval);
  }, [isPlaying, currentMeditation]);
  
  // Initialize audio element
  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;
    
    const handleTimeUpdate = () => {
      if (!audio) return;
      setCurrentTime(audio.currentTime);
      setProgress((audio.currentTime / (audio.duration || 1)) * 100);

      // Progress means the stream is healthy — reset stall recovery
      stallRecoveryAttemptsRef.current = 0;
      if (stallRecoveryTimerRef.current) {
        clearTimeout(stallRecoveryTimerRef.current);
        stallRecoveryTimerRef.current = null;
      }

      // Keep Android's media framework updated with current position so it
      // maintains audio focus and continues playback when the screen is off
      if ('mediaSession' in navigator && audio.duration) {
        navigator.mediaSession.setPositionState({
          duration: audio.duration,
          playbackRate: audio.playbackRate,
          position: audio.currentTime,
        });
      }
    };

    const handleDurationChange = () => {
      if (!audio) return;
      setDuration(audio.duration);
    };

    const handleEnded = () => {
      setIsPlaying(false);
      setCurrentTime(0);
      setProgress(0);
    };

    // The OS can pause the element without the app knowing (screen lock,
    // Doze while charging). Mirror the element's real state so the UI never
    // shows a frozen "playing" button. But ignore the transient pause that
    // audio.load() emits during our own stall recovery — that is not a real stop.
    const handlePause = () => {
      if (isRecoveringHtml5Ref.current) return;
      setIsPlaying(false);
      if ('mediaSession' in navigator) {
        navigator.mediaSession.playbackState = 'paused';
      }
    };

    const handlePlaying = () => {
      setIsPlaying(true);
      if ('mediaSession' in navigator) {
        navigator.mediaSession.playbackState = 'playing';
      }
    };

    // Re-buffer and resume a stream that died (network stall or error)
    const recoverStalledStream = () => {
      if (!audio.src || audio.ended) return;
      if (stallRecoveryAttemptsRef.current >= 3) return;
      stallRecoveryAttemptsRef.current += 1;

      const resumeAt = audio.currentTime;
      const shouldResume = isPlayingRef.current;

      // Suppress the pause event that load() emits so the UI doesn't flicker to
      // "paused" during the rebuffer. Cleared once we resume or give up.
      isRecoveringHtml5Ref.current = true;

      const cleanup = () => {
        audio.removeEventListener('loadedmetadata', handleLoadedMetadata);
        audio.removeEventListener('error', handleRecoveryError);
        isRecoveringHtml5Ref.current = false;
      };

      const handleLoadedMetadata = () => {
        cleanup();
        audio.currentTime = resumeAt;
        if (shouldResume) {
          audio.play().catch(error => {
            console.error('Error resuming audio after stall recovery:', error);
          });
        }
      };

      // If the reload itself fails (dead src), stop pretending to play so the UI
      // reflects reality, and drop the listeners rather than leaking them.
      const handleRecoveryError = () => {
        cleanup();
        setIsPlaying(false);
        if ('mediaSession' in navigator) {
          navigator.mediaSession.playbackState = 'paused';
        }
      };

      audio.addEventListener('loadedmetadata', handleLoadedMetadata);
      audio.addEventListener('error', handleRecoveryError);
      audio.load();
    };

    const scheduleStallRecovery = () => {
      if (stallRecoveryTimerRef.current) return;
      const stalledAt = audio.currentTime;
      stallRecoveryTimerRef.current = setTimeout(() => {
        stallRecoveryTimerRef.current = null;
        // Recover only if playback is still stuck where it stalled
        if (isPlayingRef.current && !audio.paused && audio.currentTime === stalledAt && !audio.ended) {
          recoverStalledStream();
        }
      }, 10000);
    };

    const handleError = () => {
      console.error('Audio element error:', audio.error);
      // A recovery reload in flight has its own one-shot error handler; don't
      // double-drive recovery from here.
      if (isRecoveringHtml5Ref.current) return;
      recoverStalledStream();
    };

    audio.addEventListener('timeupdate', handleTimeUpdate);
    audio.addEventListener('durationchange', handleDurationChange);
    audio.addEventListener('ended', handleEnded);
    audio.addEventListener('pause', handlePause);
    audio.addEventListener('playing', handlePlaying);
    audio.addEventListener('stalled', scheduleStallRecovery);
    audio.addEventListener('waiting', scheduleStallRecovery);
    audio.addEventListener('error', handleError);

    return () => {
      audio.removeEventListener('timeupdate', handleTimeUpdate);
      audio.removeEventListener('durationchange', handleDurationChange);
      audio.removeEventListener('ended', handleEnded);
      audio.removeEventListener('pause', handlePause);
      audio.removeEventListener('playing', handlePlaying);
      audio.removeEventListener('stalled', scheduleStallRecovery);
      audio.removeEventListener('waiting', scheduleStallRecovery);
      audio.removeEventListener('error', handleError);
      if (stallRecoveryTimerRef.current) {
        clearTimeout(stallRecoveryTimerRef.current);
        stallRecoveryTimerRef.current = null;
      }
    };
  }, []);

  // Try to restore playback state when component mounts
  useEffect(() => {
    if (!hasAttemptedRestore.current) {
      hasAttemptedRestore.current = true;
      loadPlaybackState();
    }
  }, []);
  
  // Save playback state when changes occur
  useEffect(() => {
    // Only save state if there's an active meditation
    if (currentMeditation && currentTime > 0) {
      // Clear any existing timeout
      if (saveStateTimeoutRef.current) {
        clearTimeout(saveStateTimeoutRef.current);
      }
      
      // Set a new timeout to debounce frequent updates
      saveStateTimeoutRef.current = setTimeout(() => {
        savePlaybackState();
      }, 1000); // Save after 1 second of no updates
    }
    
    return () => {
      if (saveStateTimeoutRef.current) {
        clearTimeout(saveStateTimeoutRef.current);
      }
    };
  }, [currentMeditation, currentTime]);
  
  // Save state before unloading the page
  useEffect(() => {
    const handleBeforeUnload = () => {
      if (currentMeditation) {
        savePlaybackState();
      }
    };
    
    window.addEventListener('beforeunload', handleBeforeUnload);
    
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [currentMeditation, currentTime]);
  
  // Listen for page visibility changes to handle background/foreground playback
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'hidden') {
        // Record whether we were playing before the screen turned off
        wasPlayingBeforeHide.current = isPlaying;
        // Save state in case the OS kills the app
        savePlaybackState();
      } else if (document.visibilityState === 'visible') {
        if (isNativeAndroidAudio) {
          // Native audio keeps playing while the screen is locked; just
          // re-sync the UI with the true native state (position advanced,
          // or the OS paused it)
          syncNativePlaybackState();
          return;
        }
        // Screen was unlocked — if Android suspended the audio while we were
        // "playing", try to resume automatically
        const audio = audioRef.current;
        if (wasPlayingBeforeHide.current && audio && audio.paused && currentMeditation) {
          audio.play()
            .then(() => {
              setIsPlaying(true);
              if ('mediaSession' in navigator) {
                navigator.mediaSession.playbackState = 'playing';
              }
            })
            .catch(error => {
              console.error('Error resuming audio after screen unlock:', error);
            });
        }
      }
    };

    // Define the beforeunload handler in this scope
    const handleBeforeUnload = () => {
      if (currentMeditation) {
        savePlaybackState();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [currentMeditation, currentTime, isPlaying]);
  
  // Listen for user logout event to stop playback and hide mini player
  useEffect(() => {
    const handleUserLogout = () => {
      stopMeditation();
      hideMiniPlayer();
    };
    
    window.addEventListener('user-logout', handleUserLogout);
    
    return () => {
      window.removeEventListener('user-logout', handleUserLogout);
    };
  }, []);
  
  // Handle volume changes
  useEffect(() => {
    if (isNativeAndroidAudio) {
      if (nativeAudioCreatedRef.current) {
        AudioPlayer.setVolume({ audioId: NATIVE_AUDIO_ID, volume: volume / 100 }).catch(error => {
          console.error('Error setting native audio volume:', error);
        });
      }
      return;
    }
    const audio = audioRef.current;
    if (audio) {
      audio.volume = volume / 100;
    }
  }, [volume]);

  // Setup media session controls (on Android the native foreground service
  // owns the media session, so skip the WebView one there)
  useEffect(() => {
    if (!isNativeAndroidAudio && 'mediaSession' in navigator && currentMeditation) {
      navigator.mediaSession.metadata = new MediaMetadata({
        title: currentMeditation.title,
        artist: 'Rupert Spira',
        album: 'Luminous',
        artwork: [
          { src: '/apple-touch-icon.png', sizes: '180x180', type: 'image/png' },
          { src: '/logo192.png', sizes: '192x192', type: 'image/png' },
          { src: '/logo512.png', sizes: '512x512', type: 'image/png' },
        ]
      });

      navigator.mediaSession.setActionHandler('play', resumeMeditation);
      navigator.mediaSession.setActionHandler('pause', pauseMeditation);
      
      navigator.mediaSession.setActionHandler('seekto', (details) => {
        if (audioRef.current && details.seekTime !== undefined) {
          audioRef.current.currentTime = details.seekTime;
        }
      });
      
      return () => {
        navigator.mediaSession.setActionHandler('play', null);
        navigator.mediaSession.setActionHandler('pause', null);
        navigator.mediaSession.setActionHandler('seekto', null);
      };
    }
  }, [currentMeditation]);

  const registerMeditation = (meditation: MeditationResponse) => {
    if (currentMeditation?.hash !== meditation.hash) {
      hasTrackedPlay.current = false;
      setCurrentTime(0);
      setProgress(0);
    }
    setCurrentMeditation(meditation);
    setDuration(meditation.duration || 0);
    setIsMiniPlayerVisible(true);
  };

  const playMeditationNative = async (meditation: MeditationResponse) => {
    try {
      // Ask for notification permission before the player posts its media
      // notification, so the lock-screen / shade controls can appear.
      await ensureNotificationPermission();

      if (currentMeditation?.hash !== meditation.hash) {
        // New meditation: reset and build a fresh native player
        setCurrentTime(0);
        setProgress(0);
        hasTrackedPlay.current = false;
        setCurrentMeditation(meditation);
        setDuration(meditation.duration || 0);
        nativePendingSeekRef.current = null;
        await createNativeAudio(meditation);
      } else if (!nativeAudioCreatedRef.current) {
        // Same meditation restored from storage: resume from saved position
        nativePendingSeekRef.current = currentTime > 0 ? currentTime : null;
        await createNativeAudio(meditation);
      }

      await AudioPlayer.play({ audioId: NATIVE_AUDIO_ID });
      setIsPlaying(true);
      setIsMiniPlayerVisible(true);

      // Track that user started listening to this meditation
      if (meditation.hash) {
        meditationService.trackStarted(meditation.hash)
          .catch(error => {
            console.error('Error tracking meditation start:', error);
            // Non-critical error, don't interrupt playback
          });
      }
    } catch (error) {
      console.error('Error playing audio natively:', error);
    }
  };

  const playMeditation = (meditation: MeditationResponse) => {
    if (isNativeAndroidAudio) {
      playMeditationNative(meditation);
      return;
    }

    const audio = audioRef.current;
    if (!audio) return;
    
    // If a different meditation is already playing, reset the audio
    if (currentMeditation?.hash !== meditation.hash) {
      audio.pause();
      setCurrentTime(0);
      setProgress(0);
      hasTrackedPlay.current = false;
      
      // Set the new meditation data
      setCurrentMeditation(meditation);
      
      // Set the audio source
      audio.src = meditation.audioUrl || '';
      audio.load();
    }
    
    // Play the audio
    audio.play()
      .then(() => {
        setIsPlaying(true);
        setIsMiniPlayerVisible(true);

        // Tell Android's media framework we are actively playing so it keeps
        // audio alive when the screen turns off
        if ('mediaSession' in navigator) {
          navigator.mediaSession.playbackState = 'playing';
        }

        // Track that user started listening to this meditation
        if (meditation.hash) {
          meditationService.trackStarted(meditation.hash)
            .catch(error => {
              console.error('Error tracking meditation start:', error);
              // Non-critical error, don't interrupt playback
            });
        }
      })
      .catch(error => {
        console.error('Error playing audio:', error);
      });
  };

  const pauseMeditation = () => {
    if (isNativeAndroidAudio) {
      if (nativeAudioCreatedRef.current && isPlaying) {
        AudioPlayer.pause({ audioId: NATIVE_AUDIO_ID }).catch(error => {
          console.error('Error pausing native audio:', error);
        });
        setIsPlaying(false);
      }
      return;
    }

    const audio = audioRef.current;
    if (audio && isPlaying) {
      audio.pause();
      setIsPlaying(false);
      
      // Update navigator.mediaSession playback state
      if ('mediaSession' in navigator) {
        navigator.mediaSession.playbackState = 'paused';
      }
    }
  };

  const resumeMeditationNative = async () => {
    if (isPlaying || !currentMeditation) return;
    try {
      if (!nativeAudioCreatedRef.current) {
        // Player not built yet (e.g. session restored from storage)
        nativePendingSeekRef.current = currentTime > 0 ? currentTime : null;
        await createNativeAudio(currentMeditation);
      }
      await AudioPlayer.play({ audioId: NATIVE_AUDIO_ID });
      setIsPlaying(true);
    } catch (error) {
      console.error('Error resuming native playback:', error);
    }
  };

  const resumeMeditation = () => {
    if (isNativeAndroidAudio) {
      resumeMeditationNative();
      return;
    }

    const audio = audioRef.current;
    if (audio && !isPlaying && currentMeditation) {
      audio.play()
        .then(() => {
          setIsPlaying(true);
          
          // Update navigator.mediaSession playback state
          if ('mediaSession' in navigator) {
            navigator.mediaSession.playbackState = 'playing';
          }
        })
        .catch(error => {
          console.error('Error resuming playback:', error);
        });
    }
  };

  const stopMeditation = () => {
    if (isNativeAndroidAudio) {
      if (nativeAudioCreatedRef.current) {
        AudioPlayer.stop({ audioId: NATIVE_AUDIO_ID })
          .catch(error => {
            console.error('Error stopping native audio:', error);
          })
          .finally(() => {
            // Tear down so the foreground service and notification go away
            destroyNativeAudio();
          });
      }
      setIsPlaying(false);
      setCurrentTime(0);
      setProgress(0);
      return;
    }

    const audio = audioRef.current;
    if (audio) {
      audio.pause();
      audio.currentTime = 0;
      setIsPlaying(false);
      setCurrentTime(0);
      setProgress(0);
      
      // Update navigator.mediaSession playback state
      if ('mediaSession' in navigator) {
        navigator.mediaSession.playbackState = 'none';
      }
    }
  };

  const setCurrentTimeManually = (time: number) => {
    if (isNativeAndroidAudio) {
      if (nativeAudioCreatedRef.current) {
        AudioPlayer.seek({ audioId: NATIVE_AUDIO_ID, timeInSeconds: time }).catch(error => {
          console.error('Error seeking native audio:', error);
        });
      } else {
        // Apply once the player is created on the next play
        nativePendingSeekRef.current = time;
      }
      setCurrentTime(time);
      setProgress((time / (duration || 1)) * 100);
      return;
    }

    const audio = audioRef.current;
    if (audio) {
      audio.currentTime = time;
      setCurrentTime(time);
      setProgress((time / (audio.duration || 1)) * 100);
    }
  };

  const togglePlayPause = () => {
    if (isPlaying) {
      pauseMeditation();
    } else {
      resumeMeditation();
    }
  };

  const hideMiniPlayer = () => {
    setIsMiniPlayerVisible(false);
  };

  const showMiniPlayer = () => {
    if (currentMeditation) {
      setIsMiniPlayerVisible(true);
    }
  };

  const value = {
    currentMeditation,
    isPlaying,
    currentTime,
    duration,
    progress,
    volume,
    isMiniPlayerVisible,
    audioRef,
    isAudioReady,
    audioEndedToken,
    audioEndedHash,

    registerMeditation,
    playMeditation,
    pauseMeditation,
    resumeMeditation,
    stopMeditation,
    setCurrentTime: setCurrentTimeManually,
    setVolume,
    togglePlayPause,
    hideMiniPlayer,
    showMiniPlayer,
  };

  return (
    <PlaybackContext.Provider value={value}>
      {children}
      <audio ref={audioRef} preload="none" />
    </PlaybackContext.Provider>
  );
};

export const usePlayback = () => {
  const context = useContext(PlaybackContext);
  if (context === undefined) {
    throw new Error('usePlayback must be used within a PlaybackProvider');
  }
  return context;
};
